import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Download, Check, ArrowRight, ShieldCheck, Sparkles, Coins, Lock, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import { trackLeadConversion } from "@/lib/analytics";
import { metaLead } from "@/lib/metaPixel";
import { getAttribution } from "@/lib/attribution";
import { setUserRole } from "@/lib/userRole";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUNDLE_ID = "cash-bundle";

const SEO_TITLE = "Cash-Bundle für Pflegedienste · Abrechnungs-Atlas + Widerspruchs-Guide kostenlos | Dickies-Campus";
const SEO_DESC = "Zwei kostenlose B2B-Leitfäden in einem Schritt: Abrechnungs-Atlas (alle 16 Bundesländer) + Pflegekassen-Kürzungen Musterwiderspruch-Guide (12 Mustertexte). Premium-PDFs gratis im Dickies-Campus.";

const ROLES = [
  ["pdl",         "PDL / Leitung"],
  ["abrechnung",  "Abrechnung"],
  ["inhaber",     "Inhaber:in / GF"],
  ["andere",      "Andere"],
];

const ATLAS_PREVIEW = "/leitfaden-abrechnungs-atlas-preview.webp";
const WIDERSPRUCH_PREVIEW = "/leitfaden-kuerzungen-widerspruch-preview.webp";

export default function LpCashBundle() {
  const [form, setForm] = useState({ name: "", email: "", role: "pdl", newsletter: false, phone: "", bundesland: "", company: "", position: "" });
  const [state, setState] = useState({ loading: false, success: false, error: null, response: null });

  useEffect(() => {
    document.title = SEO_TITLE;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = SEO_DESC;
    window.scrollTo(0, 0);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, success: false, error: null, response: null });
    try {
      const res = await axios.post(`${API}/leadmagnet-bundle/${BUNDLE_ID}`, {
        email: form.email,
        name: form.name,
        role: form.role,
        phone: form.phone,
        bundesland: form.bundesland,
        company: form.company,
        position: form.position,
        consent: true,
        newsletter_opt_in: form.newsletter,
        attribution: getAttribution(),
      });
      // DOI: kein direkter Download mehr
      trackLeadConversion(10, `leadmagnet-bundle:${BUNDLE_ID}`);
      metaLead(`leadmagnet-bundle:${BUNDLE_ID}`);
      setUserRole(form.role);
      setState({ loading: false, success: true, error: null, response: res.data });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = (status === 400 && typeof detail === "string") ? detail
        : status === 422 ? "Bitte gültige E-Mail eingeben."
        : "Bitte später erneut versuchen.";
      setState({ loading: false, success: false, error: msg, response: null });
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />

      {/* HERO + FORM */}
      <section className="relative pt-28 md:pt-36 pb-20 md:pb-28 bg-white overflow-hidden">
        {/* subtle accent */}
        <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-brand-rose/30 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          <Reveal>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra" data-testid="cash-bundle-kicker">
                  Kostenlos · 2 PDFs · 1 E-Mail · Für Pflegedienste
                </span>
              </div>

              <h1 className="font-display text-[2.4rem] sm:text-5xl lg:text-[3.4rem] text-sand-800 font-normal tracking-tight leading-[1.05]"
                  data-testid="cash-bundle-h1">
                Pflegekasse kürzt? <br />
                <span className="text-terra italic">Hol dir das Cash zurück.</span>
              </h1>

              <p className="mt-7 text-sand-700 text-[16px] sm:text-[17px] leading-[1.65] max-w-xl">
                Die zwei wichtigsten B2B-Leitfäden in einem Schritt &#8211; gratis als PDF.
                Der <b>Abrechnungs-Atlas</b> zeigt für alle 16 Bundesländer, <i>wo</i> Kürzung droht.
                Der <b>Widerspruchs-Guide</b> gibt dir die <b>12 Musterwidersprüche</b>, <i>wie</i> du sie
                zurückholst &#8211; bis zur Schiedsstelle oder zum Sozialgericht.
              </p>

              <ul className="mt-8 space-y-3.5 text-[15px] text-sand-700">
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-terra/10 text-terra shrink-0">
                    <Coins className="h-4 w-4" strokeWidth={2} />
                  </span>
                  <span>
                    <span className="font-semibold text-sand-800">2.640 €&nbsp;in 6 Wochen zurückgeholt.</span>{" "}
                    <span className="text-sand-600">Hero-Case aus der NRW-Community &#8211; 4 von 5 Widersprüchen erfolgreich.</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-terra/10 text-terra shrink-0">
                    <ShieldCheck className="h-4 w-4" strokeWidth={2} />
                  </span>
                  <span>
                    <span className="font-semibold text-sand-800">Atlas + Widerspruchs-Guide gehören zusammen.</span>{" "}
                    <span className="text-sand-600">Der eine sagt dir wo &#8211; der andere wie. Perfekte Cash-Verteidigung.</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-terra/10 text-terra shrink-0">
                    <Sparkles className="h-4 w-4" strokeWidth={2} />
                  </span>
                  <span>
                    <span className="font-semibold text-sand-800">Vorschauen sofort &#8211; Vollversionen gratis im Campus.</span>{" "}
                    <span className="text-sand-600">Für Campus-Mitglieder — Zugang mit jedem Paket.</span>
                  </span>
                </li>
              </ul>

              <div className="mt-9 flex items-center gap-3 text-[12.5px] text-sand-500">
                <span className="inline-flex h-2 w-2 rounded-full bg-brand-rose animate-pulse" />
                Geschrieben für ambulante Pflegedienste, die jede Kürzung der Pflegekasse satzweise prüfen.
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="relative">
              {/* Two PDF covers, slightly fanned */}
              <div className="absolute -top-10 -left-6 hidden lg:flex gap-3 z-10">
                <div className="w-32 rounded-2xl overflow-hidden border border-cream-300 shadow-soft -rotate-[6deg] bg-white">
                  <img src={ATLAS_PREVIEW} alt="Abrechnungs-Atlas Vorschau" className="w-full h-auto block"
                       onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </div>
                <div className="w-32 rounded-2xl overflow-hidden border border-cream-300 shadow-soft rotate-[6deg] mt-6 bg-white">
                  <img src={WIDERSPRUCH_PREVIEW} alt="Widerspruchs-Guide Vorschau" className="w-full h-auto block"
                       onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </div>
              </div>

              <div className="rounded-3xl bg-cream-50 border border-cream-300 shadow-hover p-7 md:p-9" data-testid="cash-bundle-form-card">
                {!state.success ? (
                  <>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-clay-dark mb-3 flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5" strokeWidth={2} />
                      Premium-Bundle · Vorschauen anfordern
                    </div>
                    <h2 className="font-display text-2xl md:text-[1.7rem] text-sand-800 leading-tight">
                      Beide Vorschauen sofort. Vollversionen gratis im Campus.
                    </h2>
                    <p className="mt-3 text-[13.5px] text-sand-600 leading-relaxed">
                      Eine E-Mail &#8211; du bekommst <b>beide 4-Seiten-Vorschauen</b> sofort
                      und freischaltbar im Campus die kompletten Versionen (Atlas 28&nbsp;S. + Widerspruchs-Guide 16&nbsp;S.).
                    </p>

                    <form onSubmit={submit} className="mt-7 grid gap-4">
                      <div>
                        <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">
                          Du bist …
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {ROLES.map(([v, l]) => (
                            <button
                              key={v} type="button"
                              onClick={() => setForm({ ...form, role: v })}
                              data-testid={`cash-bundle-role-${v}`}
                              className={`rounded-full px-3 h-10 text-[12.5px] font-semibold border transition-all ${
                                form.role === v
                                  ? "border-terra bg-terra text-white"
                                  : "border-cream-300 text-sand-700 hover:border-clay bg-white"
                              }`}
                            >{l}</button>
                          ))}
                        </div>
                      </div>

                      <input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Vor- und Nachname *"
                        required minLength={2}
                        data-testid="cash-bundle-name"
                        className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                      />
                      <input
                        required type="email" value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="E-Mail-Adresse *"
                        data-testid="cash-bundle-email"
                        className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                      />
                      <input
                        required type="tel" inputMode="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="Telefon (z. B. 0211 12345678) *"
                        data-testid="cash-bundle-phone"
                        className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                      />
                      <select
                        required value={form.bundesland}
                        onChange={(e) => setForm({ ...form, bundesland: e.target.value })}
                        data-testid="cash-bundle-bundesland"
                        className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                      >
                        <option value="">Bundesland * (bitte wählen)</option>
                        <option value="BW">Baden-Württemberg</option>
                        <option value="BY">Bayern</option>
                        <option value="BE">Berlin</option>
                        <option value="BB">Brandenburg</option>
                        <option value="HB">Bremen</option>
                        <option value="HH">Hamburg</option>
                        <option value="HE">Hessen</option>
                        <option value="MV">Mecklenburg-Vorpommern</option>
                        <option value="NI">Niedersachsen</option>
                        <option value="NW">Nordrhein-Westfalen</option>
                        <option value="RP">Rheinland-Pfalz</option>
                        <option value="SL">Saarland</option>
                        <option value="SN">Sachsen</option>
                        <option value="ST">Sachsen-Anhalt</option>
                        <option value="SH">Schleswig-Holstein</option>
                        <option value="TH">Thüringen</option>
                        <option value="AT">Österreich</option>
                        <option value="CH">Schweiz</option>
                        <option value="OTHER">Anderes</option>
                      </select>
                      {(form.role === "pdl" || form.role === "leitung") && (
                        <>
                          <input
                            required value={form.company}
                            onChange={(e) => setForm({ ...form, company: e.target.value })}
                            placeholder="Pflegedienst / Einrichtung *"
                            data-testid="cash-bundle-company"
                            className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                          />
                          <input
                            required value={form.position}
                            onChange={(e) => setForm({ ...form, position: e.target.value })}
                            placeholder="Position (z. B. PDL, WBL) *"
                            data-testid="cash-bundle-position"
                            className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                          />
                        </>
                      )}

                      <label className="flex items-start gap-3 text-[13px] text-sand-600 leading-snug cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={form.newsletter}
                          onChange={(e) => setForm({ ...form, newsletter: e.target.checked })}
                          data-testid="cash-bundle-newsletter"
                          className="mt-1 h-4 w-4 accent-terra shrink-0"
                        />
                        <span>
                          <b>Helden-Briefing abonnieren</b> &#8211; 1× pro Monat ein PDL-Update zu MD,
                          Abrechnung &amp; Personal. Abmelden jederzeit per Link in jeder Mail.
                        </span>
                      </label>

                      <Button
                        type="submit"
                        disabled={state.loading}
                        data-testid="cash-bundle-submit"
                        className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-6 text-[15px] font-semibold mt-1"
                      >
                        {state.loading ? "Wird gesendet …" : (
                          <span className="inline-flex items-center gap-2">
                            <Download className="h-4 w-4" strokeWidth={1.8} />
                            Beide Vorschauen per E-Mail anfordern
                          </span>
                        )}
                      </Button>

                      {state.error && (
                        <p className="text-[13px] text-terra font-medium" data-testid="cash-bundle-error">
                          {state.error}
                        </p>
                      )}
                      <p className="text-[11px] text-sand-500 leading-snug">
                        Mit dem Absenden stimmst du der Verarbeitung deiner Daten gemäß{" "}
                        <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link> zu.
                        Wir senden dir eine Bestätigungs-Mail — erst nach Klick bekommst du die PDFs.
                      </p>
                    </form>
                  </>
                ) : (
                  <div data-testid="cash-bundle-success" className="py-2">
                    <div className="flex flex-col items-center text-center">
                      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-rose/30 text-brand-darkred">
                        <Check className="h-7 w-7" strokeWidth={2} />
                      </div>
                      <h3 className="mt-5 font-display text-2xl text-sand-800">Fast geschafft!</h3>
                      <p className="mt-3 text-sand-600 text-[14px] leading-relaxed max-w-md">
                        Wir haben dir gerade eine E-Mail an <strong className="text-sand-800">{form.email}</strong> geschickt.
                        Bitte klicke darin auf den Bestätigungs-Link — danach bekommst du beide Vorschauen sofort.
                      </p>
                      <p className="mt-2 text-[12px] text-sand-500">
                        Keine Mail erhalten? Schau bitte auch im Spam-Ordner.
                      </p>
                    </div>

                    <div className="mt-6 rounded-2xl bg-brand-darkred text-white p-6">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brand-rose mb-3 flex items-center gap-2">
                        <Lock className="h-3.5 w-3.5" strokeWidth={2} />
                        Vollversionen im Campus
                      </div>
                      <p className="text-[14.5px] leading-relaxed">
                        Beide kompletten Versionen (Atlas&nbsp;28&nbsp;S. + Widerspruchs-Guide&nbsp;16&nbsp;S.)
                        liegen im Dickies-Campus &#8212; Zugang für Mitglieder.
                      </p>
                      <Link to="/preise"
                            data-testid="cash-bundle-register-cta"
                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-brand-darkred px-5 h-11 text-[14px] font-semibold hover:bg-brand-rose/30 transition">
                        Mitglied werden <ArrowRight className="h-4 w-4" />
                      </Link>
                      <p className="mt-4 text-[11.5px] text-brand-rose/80">
                        Schon Mitglied?{" "}
                        <Link to="/campus/login" className="underline text-white font-semibold">
                          Hier einloggen
                        </Link>{" "}
                        und beide Vollversionen direkt laden.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT'S IN THE BUNDLE */}
      <section className="bg-cream-50 py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra mb-3">
                Was du bekommst
              </div>
              <h2 className="font-display text-3xl md:text-[2.6rem] text-sand-800 leading-[1.15]">
                Zwei Leitfäden &#8211; ein Verteidigungs-Konzept.
              </h2>
              <p className="mt-5 text-sand-700 text-[15px] leading-[1.7]">
                Wir haben die beiden in der Helden-Community am häufigsten genutzten B2B-PDFs gebündelt.
                Sie funktionieren einzeln, aber zusammen erst richtig.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid lg:grid-cols-2 gap-7">
            <Reveal>
              <div className="rounded-3xl bg-white border border-cream-300 p-8 h-full">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-clay-dark">
                  PDF 1 · 28 Seiten · Vollversion
                </div>
                <h3 className="mt-3 font-display text-2xl text-sand-800">Abrechnungs-Atlas</h3>
                <p className="mt-2 text-sand-600 text-[14.5px] leading-relaxed">
                  Wegepauschale morgens + abends? LK 3 + LK 4 erlaubt? Hier siehst du in 3 Sekunden,
                  wie streng dein Bundesland ist &#8211; mit Vertragsquelle nach § 75 oder § 89 SGB XI hinterlegt.
                </p>
                <ul className="mt-5 space-y-2.5 text-[14px] text-sand-700">
                  {[
                    "16 Bundesländer · jede Vertragsquelle belegt",
                    "Hero-Case mit amtlicher Auflösung (NRW · LK 15a)",
                    "3-Gruppen-Klassifizierung der BL (restriktiv · mittel · einsatzbezogen)",
                    "Top 8 Stolperfallen + Mustertext für Begründungen",
                  ].map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-terra mt-0.5 shrink-0" strokeWidth={2.4} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/leitfaden/abrechnungs-atlas" data-testid="cash-bundle-atlas-single"
                      className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra underline">
                  Einzeln ansehen <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="rounded-3xl bg-white border border-cream-300 p-8 h-full">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-clay-dark">
                  PDF 2 · 16 Seiten · Vollversion
                </div>
                <h3 className="mt-3 font-display text-2xl text-sand-800">Pflegekassen-Kürzungen &middot; Musterwiderspruch-Guide</h3>
                <p className="mt-2 text-sand-600 text-[14.5px] leading-relaxed">
                  12 typische Kürzungsgründe, zu jedem ein sofort einsetzbarer Musterwiderspruch
                  zum Anpassen. Plus Fristen-Übersicht nach SGB X und der 3-Stufen-Eskalations-Plan
                  bis zum Sozialgericht.
                </p>
                <ul className="mt-5 space-y-2.5 text-[14px] text-sand-700">
                  {[
                    "12 Musterwidersprüche (formell · inhaltlich · Doku)",
                    "Fristen nach § 84 SGB X (1 Monat, sonst rechtskräftig)",
                    "Eskalation: Schiedsstelle § 76 SGB XI · Sozialgericht §§ 51, 87 SGG",
                    "Hero-Case: 2.640 € in 6 Wochen zurückgeholt",
                  ].map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-terra mt-0.5 shrink-0" strokeWidth={2.4} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/leitfaden/kuerzungen-widerspruch" data-testid="cash-bundle-widerspruch-single"
                      className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra underline">
                  Einzeln ansehen <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="bg-brand-rose py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <blockquote className="font-display italic text-2xl md:text-[1.85rem] text-brand-darkred leading-[1.4] text-center">
              „Vier Widersprüche, vier Erfolge. Die Mustertexte musste ich nur anpassen. In sechs Wochen 2.640 € auf dem Konto &#8211; ohne Anwalt."
            </blockquote>
            <p className="mt-7 text-center text-white text-[13px] uppercase tracking-[0.22em] font-semibold">
              PDL · ambulanter Pflegedienst · NRW (anonymisiert)
            </p>
          </Reveal>
        </div>
      </section>

      {/* CAMPUS BRIDGE */}
      <section className="bg-white py-28 md:py-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-10 bg-terra" />
                  <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                    Das ist erst der Anfang
                  </span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.6rem] text-sand-800 font-normal tracking-tight leading-[1.1]">
                  Im Dickies-Campus wartet die <span className="text-terra">ganze Bibliothek</span>.
                </h2>
                <p className="mt-6 text-sand-700 text-[15px] leading-[1.7] max-w-xl">
                  Atlas + Widerspruchs-Guide sind nur zwei von &#252;ber 8 Premium-PDFs in der
                  Schatzkiste. Plus MD-Survival-Guide, Schmerz- und Sturz-Leitfäden, Pflegesatz-Matrix &amp; mehr.
                  Alles inklusive in der Campus-Mitgliedschaft.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/preise" data-testid="cash-bundle-bridge-register">
                    <Button className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-7 text-[15px] font-semibold">
                      <span className="inline-flex items-center gap-2">
                        Pakete &amp; Preise ansehen <ArrowRight className="h-4 w-4" />
                      </span>
                    </Button>
                  </Link>
                  <Link to="/leitfaeden" data-testid="cash-bundle-bridge-bibliothek">
                    <Button variant="outline"
                            className="rounded-full border-clay text-sand-800 hover:bg-cream-100 h-12 px-7 text-[15px] font-semibold">
                      Alle Leitfäden ansehen
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  ["Premium-PDFs im Campus", "8+"],
                  ["Atlas-Bundesländer", "16/16"],
                  ["Mustertexte Widerspruch", "12"],
                  ["Hero-Case Cash zurück", "2.640 €"],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-3xl bg-cream-50 border border-cream-300 p-6 shadow-soft">
                    <div className="font-display text-[2rem] text-terra leading-none">{v}</div>
                    <div className="mt-2 text-[13px] uppercase tracking-[0.2em] font-bold text-clay-dark">
                      {k}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
